import { REGIONS, ENVS } from '@adsk/acs-sc-web-platform-api';
import Theme from '@adsk/alloy-react-theme';

type EnvConfig = {
  [key in keyof typeof REGIONS]: {
    [key in keyof typeof ENVS]: {
      assetsBackend: string;
      assetsFrontend: string;
    };
  };
};

const usConfig: EnvConfig['US'] = {
  QA: {
    assetsBackend:
      'https://developer-stg.api.autodesk.com/construction/assets-qa/v1/projects',
    assetsFrontend: 'https://acc-qa.autodesk.com/build/assets/projects',
  },
  STAGING: {
    assetsBackend:
      'https://developer-stg.api.autodesk.com/construction/assets/v1/projects',
    assetsFrontend: 'https://acc-staging.autodesk.com/build/assets/projects',
  },
  PRODUCTION: {
    assetsBackend:
      'https://developer.api.autodesk.com/construction/assets/v1/projects',
    assetsFrontend: 'https://acc.autodesk.com/build/assets/projects',
  },
};

const emeaConfig: EnvConfig['EMEA'] = {
  ...usConfig,
  PRODUCTION: {
    ...usConfig.PRODUCTION,
    assetsFrontend: 'https://acc.autodesk.eu/build/assets/projects',
  },
};

const apacConfig: EnvConfig['APAC'] = {
  ...usConfig,
  STAGING: {
    ...usConfig.STAGING,
    assetsFrontend:
      'https://acc-staging.apac.autodesk.com/build/assets/projects',
  },
  PRODUCTION: {
    ...usConfig.PRODUCTION,
    assetsFrontend: 'https://acc.apac.autodesk.com/build/assets/projects',
  },
};

const ausConfig: EnvConfig['AUS'] = {
  ...usConfig,
  STAGING: {
    ...usConfig.STAGING,
    assetsFrontend:
      'https://acc-staging.aus.autodesk.com/build/assets/projects',
  },
  PRODUCTION: {
    ...usConfig.PRODUCTION,
    assetsFrontend: 'https://acc.aus.autodesk.com/build/assets/projects',
  },
};

export const envConfig: EnvConfig = {
  US: usConfig,
  EMEA: emeaConfig,
  APAC: apacConfig,
  AUS: ausConfig,
};

const buildStatusColors = {
  'adsk-charcoal-900': Theme.colors.charcoal900,
  'adsk-charcoal-500': Theme.colors.charcoal500,
  'adsk-charcoal-200': Theme.colors.charcoal200,
  'adsk-red-500': Theme.colors.red500,
  'adsk-green-500': Theme.colors.green500,
  'adsk-yellow-orange-700': Theme.colors.yellowOrange700,
  'adsk-yellow-orange-500': Theme.colors.yellowOrange500,
  'adsk-yellow-orange-300': Theme.colors.yellowOrange300,
  'adsk-pink-500': Theme.colors.pink500,
  'adsk-pink-300': Theme.colors.pink300,
  'adsk-blue-700': Theme.colors.adskBlue700,
  'adsk-blue-500': Theme.colors.adskBlue500,
  'adsk-blue-100': Theme.colors.adskBlue100,
  'adsk-turquoise-700': Theme.colors.turquoise700,
  'adsk-turquoise-300': Theme.colors.turquoise300,
  'adsk-purple-700': Theme.colors.purple700,
  'adsk-purple-300': Theme.colors.purple300,
  default: Theme.colors.charcoal500,
} as const;

// Map of bim360 color names to Alloy colors
const bim360StatusColors = {
  'red-orange': buildStatusColors['adsk-red-500'],
  red: buildStatusColors['adsk-red-500'],
  magenta: buildStatusColors['adsk-pink-500'],
  indigo: buildStatusColors['adsk-purple-300'],
  purple: buildStatusColors['adsk-purple-700'],
  'dark-blue': buildStatusColors['adsk-blue-700'],
  blue: buildStatusColors['adsk-blue-500'],
  'light-blue': buildStatusColors['adsk-blue-100'],
  turquoise: buildStatusColors['adsk-turquoise-300'],
  'sea-green': buildStatusColors['adsk-turquoise-700'],
  green: buildStatusColors['adsk-green-500'],
  'light-green': buildStatusColors['adsk-green-500'],
  'yellow-green': buildStatusColors['adsk-green-500'],
  yellow: buildStatusColors['adsk-yellow-orange-300'],
  'yellow-orange': buildStatusColors['adsk-yellow-orange-500'],
  orange: buildStatusColors['adsk-yellow-orange-500'],
  brown: buildStatusColors['adsk-yellow-orange-700'],
  pink: buildStatusColors['adsk-pink-300'],
} as const;

export const assetStatusColors = {
  ...bim360StatusColors,
  ...buildStatusColors,
} as const;
